
//each snapshot is going to be limited to what type of change it is

import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Dialog, DialogType } from '@fluentui/react/lib/Dialog';
import { RootState } from 'app/store';
import ActionCard from 'components/controls/ActionCard';
import { useAuthentication } from 'global/Authentication';
import { ESnapshotStatus } from 'global/Reducer/statusSlice';
import * as IXrmOptions from 'model/AttributeOptionSets.type';
import * as React from 'react';
import { ConnectedProps, connect } from 'react-redux';

//Per teams 2/8/22 
//everything gets readonly, 
//and in addition: MNIL Data Entry Complete gets create OR reopen; 
//data entry complete gets reopen; 
//in progress gets edit; 
//everything else gets create.

type TActions =
    "compare" |
    "merge" |
    "underwriting review" |
    "view" |
    'modify' |
    'reopen and modify' |
    'create';

interface IProps extends TComponentConnectedProps {
    variant: "view-multiple-config" | "default"
    hidden?: boolean;
    isChecklist?: boolean;
    onDismiss?: (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => void;
    onConfirm?: (action: TActions) => void;
}

const EntryDialog = (props: IProps) => {

    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(false);
    const { authorizations } = useAuthentication();

    const styles = { root: { flexBasis: '0' } };

    const isUntracked = props.state.status.snapshotStatus === ESnapshotStatus.untracked;
    const snapshot = props.state.data.snapshot;
    const snapshotStatus = snapshot.statuscode;
    const snapshotChangeType = snapshot.bcbs_changetypecode;

    const dialogModalProps = { isBlocking: true, styles: { main: { maxWidth: 650 } } };

    const isPowerUser = (authorizations?.roles['BCBS - Power User']);

    function onRenderMultipleConfigEntry() {

        const dialogContentProps = {
            type: DialogType.largeHeader,
            title: 'What would you like to do with these configurations?',
            subText: '',
        };


        return (
            <Dialog minWidth={600} hidden={props.hidden ?? false} onDismiss={props.onDismiss} dialogContentProps={dialogContentProps} modalProps={dialogModalProps}>
                <Stack horizontal horizontalAlign='center' tokens={{ childrenGap: 5 }} styles={{ inner: { flex: '1' } }}>

                    {(authorizations?.canView || authorizations?.canEdit) &&
                        <Stack.Item grow={1} disableShrink styles={styles}>
                            <ActionCard
                                text='Compare'
                                icon='compare'
                                onClick={() => { props.onConfirm?.("compare"); }}
                            />
                        </Stack.Item>
                    }

                    {authorizations?.canEdit &&
                        <Stack.Item grow={1} disableShrink styles={styles}>
                            <ActionCard
                                text='Merge'
                                icon='merge'
                                onClick={() => { props.onConfirm?.("merge"); }}
                            />
                        </Stack.Item>
                    }

                    {authorizations?.canUWRC &&
                        <Stack.Item grow={1} disableShrink styles={styles}>
                            <ActionCard
                                text='Underwriting Review'
                                icon='view'
                                onClick={() => { props.onConfirm?.("underwriting review"); }}
                            />
                        </Stack.Item>
                    }

                </Stack>
            </Dialog>
        );
    }


    function onRenderDefaultEntry() {

        const dialogContentProps = {
            type: DialogType.largeHeader,
            title: 'Would you like to view or edit this configuration?',
            subText: 'Please click view to open the configuration in read only mode. Please click Create to create a new snapshot or Modify to modify an existing in-progress snapshot.',
        }

        const isPSAUser = !!authorizations?.roles['BCBS - PSA'];

        const canEditPSA = (
            snapshotChangeType === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['PSA Change(808900012)'].value
        );

        const hasUnderUnderwritingReviewStatus =
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value;

        const hasModifyStatus = (
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value && !isUntracked)
        );

        const hasReopenModifyStatus = (
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value
        );

        // 302208
        // When status = Retired, if User is NOT Power User or System Admin AND record is NOT Effective Date Gold Standard (bcbs_iseffectivedategoldstandard =/= Yes), do not show Create option.
        const hasCreateStatus = (
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value && isPowerUser && props.state.status.debug.admin) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value && snapshot.bcbs_iseffectivedategoldstandard) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value) ||
            (isUntracked)
        );

        const pendingPowerUserReview = isPowerUser && snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Pending Power User Review(808900015)'].value;


        let canView = true;
        let canModify = (authorizations?.canEdit || (isPSAUser && canEditPSA)) && hasModifyStatus && !props.isChecklist;
        let canReOpenAndModify = ((authorizations?.canEdit || (isPSAUser && canEditPSA)) && hasReopenModifyStatus) || pendingPowerUserReview;
        let canCreate = (authorizations?.canEdit || isPSAUser) && hasCreateStatus;
        let canUWRC = authorizations?.canUWRC && hasUnderUnderwritingReviewStatus;

        return (
            <Dialog minWidth={600} hidden={hideDialog} onDismiss={toggleHideDialog} dialogContentProps={dialogContentProps} modalProps={dialogModalProps}>
                <Stack horizontal horizontalAlign='center' tokens={{ childrenGap: 5 }} styles={{ inner: { flex: '1' } }} >

                    {canView &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='View'
                                icon='view'
                                onClick={() => { props.onConfirm?.("view"); }}
                                disabled={isUntracked}
                            />
                        </Stack.Item>
                    }

                    {canModify &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Modify'
                                primary={true}
                                icon='edit'
                                onClick={() => { props.onConfirm?.("modify") }}
                            />
                        </Stack.Item>
                    }

                    {canReOpenAndModify &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Reopen and Modify'
                                primary={true}
                                icon='edit'
                                onClick={() => { props.onConfirm?.("reopen and modify") }}
                            />
                        </Stack.Item>
                    }

                    {canCreate &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Create'
                                primary={true}
                                icon='edit'
                                onClick={() => { props.onConfirm?.("create") }}
                            />
                        </Stack.Item>
                    }

                    {canUWRC &&
                        <Stack.Item grow={1} disableShrink styles={styles}>
                            <ActionCard
                                text='Underwriting Review'
                                icon='view'
                                primary={true}
                                onClick={() => { props.onConfirm?.("underwriting review"); }}
                            />
                        </Stack.Item>
                    }

                </Stack>
            </Dialog>
        );
    }

    if (props.variant === "view-multiple-config") {
        return onRenderMultipleConfigEntry();
    }

    return onRenderDefaultEntry();
};

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;

export default connect(mapStateToProps)(EntryDialog);